// src/hooks/useEntitySearch.js
//
// One debounced, cancelable search request path shared by every entity mode in
// <Search> (Jobs, Inquiries, Quotes, Purchase Orders, Carriers, Vendors,
// Companies…). Replaces the tangled per-mode debounce/fetch effects that fired
// an uncancelled request on nearly every keystroke and could let a slow, stale
// response overwrite a newer one. Modeled on the proven pattern already in
// src/components/SearchInput.jsx (debounce + axios.CancelToken + loading state).
//
// See issue #482.

import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { apiClient } from "../utils/apiClient";

const DEFAULT_DEBOUNCE_MS = 300;

/**
 * @param {object} opts
 * @param {(term: string) => string} opts.buildQueryString  Returns the `/search`
 *        query string (without leading `?`) for a given term. Keeps the exact
 *        backend contract in the caller so the hook stays entity-agnostic.
 * @param {(data: any) => { rows: any[], personnel: any[] }} opts.parseResponse
 *        Splits the raw API payload into rows + optional personnel list.
 * @param {number} [opts.limit]         Used only to derive `isTruncated`.
 * @param {string} [opts.initialQuery]  Starting query (e.g. a URL `?q=` seed, or
 *        the Purchase Order "MH" prefix).
 * @param {boolean} [opts.autoLoad]     Fetch `initialQuery` once on mount.
 * @param {boolean} [opts.clearOnEmpty] When the box is emptied, clear results
 *        instead of fetching everything (embedded selectors / popovers).
 * @param {number} [opts.debounceMs]
 */
export default function useEntitySearch({
  buildQueryString,
  parseResponse,
  limit,
  initialQuery = "",
  autoLoad = false,
  clearOnEmpty = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
} = {}) {
  const [query, setQueryState] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const cancelSourceRef = useRef(null);
  const timerRef = useRef(null);
  const queryRef = useRef(initialQuery);
  // Monotonic id so only the most recent request is allowed to write state,
  // even if an older (cancelled) request's promise settles late.
  const seqRef = useRef(0);
  // Keep the latest builder/parser without forcing runSearch to change identity.
  const cfgRef = useRef({ buildQueryString, parseResponse });
  cfgRef.current = { buildQueryString, parseResponse };

  const resetToPristine = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel("Cleared search");
      cancelSourceRef.current = null;
    }
    seqRef.current += 1; // invalidate any in-flight response
    setResults([]);
    setPersonnel([]);
    setError(null);
    setIsLoading(false);
    setHasFetched(false);
  }, []);

  const runSearch = useCallback(async (term) => {
    const { buildQueryString, parseResponse } = cfgRef.current;

    // Cancel any request still in flight before starting a new one.
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel("Superseded by a newer search");
    }
    cancelSourceRef.current = axios.CancelToken.source();
    const seq = ++seqRef.current;

    setIsLoading(true);
    setError(null);

    try {
      const { data } = await apiClient.get(
        `/search?${buildQueryString(term)}`,
        {
          cancelToken: cancelSourceRef.current.token,
        },
      );
      if (seq !== seqRef.current) return; // a newer search superseded this one
      const parsed = parseResponse(data) || {};
      setResults(Array.isArray(parsed.rows) ? parsed.rows : []);
      setPersonnel(Array.isArray(parsed.personnel) ? parsed.personnel : []);
      setHasFetched(true);
    } catch (err) {
      if (axios.isCancel(err)) return; // expected — a newer search took over
      if (seq !== seqRef.current) return;
      setResults([]);
      setPersonnel([]);
      setError(err);
      setHasFetched(true);
    } finally {
      if (seq === seqRef.current) setIsLoading(false);
    }
  }, []);

  const setQuery = useCallback(
    (next) => {
      const value = next ?? "";
      queryRef.current = value;
      setQueryState(value);

      if (timerRef.current) clearTimeout(timerRef.current);

      if (clearOnEmpty && !value.trim()) {
        resetToPristine();
        return;
      }
      timerRef.current = setTimeout(() => runSearch(value), debounceMs);
    },
    [clearOnEmpty, debounceMs, runSearch, resetToPristine],
  );

  // Immediate search (Enter key / "Search" button). Still cancelable.
  const searchNow = useCallback(
    (term) => {
      const value = term !== undefined ? term : queryRef.current;
      queryRef.current = value;
      if (term !== undefined) setQueryState(value);
      if (timerRef.current) clearTimeout(timerRef.current);
      if (clearOnEmpty && !String(value).trim()) {
        resetToPristine();
        return;
      }
      runSearch(value);
    },
    [clearOnEmpty, runSearch, resetToPristine],
  );

  // Initial load on mount (mirrors the legacy per-mode initial fetches).
  useEffect(() => {
    if (!autoLoad) return;
    if (clearOnEmpty && !String(initialQuery).trim()) return;
    runSearch(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cancel timers and in-flight requests on unmount.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel("Component unmounted");
      }
    },
    [],
  );

  const isTruncated =
    typeof limit === "number" && limit > 0 && results.length >= limit;

  return {
    query,
    setQuery,
    searchNow,
    results,
    personnel,
    isLoading,
    error,
    isTruncated,
    hasFetched,
  };
}
